import React from 'react';
import Icon from '../../../components/AppIcon';
import AppIcon from '../../../components/AppIcon';


export default function AdditionalDetails({
  adults,
  children,
  onAdultsChange,
  onChildrenChange,
  specialRequirements,
  onSpecialRequirementsChange,
  moreDetails,
  onMoreDetailsChange,
  customerLocation,
  onCustomerLocationChange,
  travelPreferences,
  onTravelPreferencesChange,
  preferredNights,
  onPreferredNightsChange,
  errors
}) {
  const groupSizeOptions = [
    { value: '1', label: 'Solo (1 person)' },
    { value: '2', label: 'Couple (2 people)' },
    { value: '3-4', label: 'Small group (3-4 people)' },
    { value: '5-8', label: 'Medium group (5-8 people)' },
    { value: '9-15', label: 'Large group (9-15 people)' },
    { value: '16+', label: 'Very large group (16+ people)' }
  ];

  const preferenceCategories = [
    {
      id: 'activities',
      name: 'Activities',
      icon: 'Activity',
      options: [
        'Adventure sports', 'Cultural tours', 'Food & wine', 'Shopping',
        'Nightlife', 'Museums', 'Nature walks', 'Photography'
      ]
    },
    {
      id: 'transport',
      name: 'Transport',
      icon: 'Car',
      options: [
        'Private transfers', 'Public transport', 'Rental car', 'Walking tours',
        'Cycling', 'Train journeys', 'Boat trips', 'Helicopter tours'
      ]
    },
    {
      id: 'dining',
      name: 'Dining',
      icon: 'Utensils',
      options: [
        'Local cuisine', 'Fine dining', 'Street food', 'Vegetarian',
        'Vegan', 'Halal', 'Kosher', 'Gluten-free'
      ]
    }
  ];

  const handlePreferenceToggle = (category, option) => {
    const currentPreferences = travelPreferences?.[category] || [];
    const isSelected = currentPreferences?.includes(option);
    
    const updatedPreferences = isSelected
      ? currentPreferences?.filter(item => item !== option)
      : [...currentPreferences, option];
    
    onTravelPreferencesChange({
      ...travelPreferences,
      [category]: updatedPreferences
    });
  };

  return (
    <div className="space-y-8">
      {/* Travelers Section */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Number of Travelers</h3>
          <p className="text-sm text-muted-foreground mb-4">
            How many people will be traveling?
          </p>
        </div>

        {/* Adults and Children Counter */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Adults */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Adults
              <span className="text-muted-foreground ml-1">(Minimum 1)</span>
            </label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => onAdultsChange(Math.max(1, (adults || 1) - 1))}
                disabled={(adults || 1) <= 1}
                className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-all ${
                  (adults || 1) <= 1
                    ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                    : 'border-primary bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground'
                }`}
              >
                <Icon name="Minus" size={20} />
              </button>
              
              <div className="flex-1 text-center">
                <div className="text-3xl font-bold text-foreground">{adults || 1}</div>
                <div className="text-sm text-muted-foreground">Adult{(adults || 1) !== 1 ? 's' : ''}</div>
              </div>
              
              <button
                type="button"
                onClick={() => onAdultsChange((adults || 1) + 1)}
                className="w-12 h-12 rounded-lg border-2 border-primary bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
              >
                <Icon name="Plus" size={20} />
              </button>
            </div>
          </div>

          {/* Children */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Children
              <span className="text-muted-foreground ml-1">(Ages 0-17)</span>
            </label>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => onChildrenChange(Math.max(0, (children || 0) - 1))}
                disabled={(children || 0) <= 0}
                className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-all ${
                  (children || 0) <= 0
                    ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                    : 'border-primary bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground'
                }`}
              >
                <Icon name="Minus" size={20} />
              </button>
              
              <div className="flex-1 text-center">
                <div className="text-3xl font-bold text-foreground">{children || 0}</div>
                <div className="text-sm text-muted-foreground">Child{(children || 0) !== 1 ? 'ren' : ''}</div>
              </div>
              
              <button
                type="button"
                onClick={() => onChildrenChange((children || 0) + 1)}
                className="w-12 h-12 rounded-lg border-2 border-primary bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
              >
                <Icon name="Plus" size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Total travelers summary */}
        <div className="mt-4 p-4 bg-primary/10 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="Users" size={20} className="text-primary" />
            <span className="font-semibold text-primary">
              Total Travelers: {(adults || 1) + (children || 0)} 
              <span className="text-sm font-normal ml-2">
                ({adults || 1} Adult{(adults || 1) !== 1 ? 's' : ''}, {children || 0} Child{(children || 0) !== 1 ? 'ren' : ''})
              </span>
            </span>
          </div>
        </div>

        {errors?.adults && (
          <p className="text-error text-sm mt-2">{errors?.adults}</p>
        )}
        {errors?.children && (
          <p className="text-error text-sm mt-2">{errors?.children}</p>
        )}
      </div>

      {/* ✅ NEW: Preferred Number of Nights Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Preferred Number of Nights</h3>
          <p className="text-sm text-muted-foreground mb-4">
            How many nights would you ideally like to stay?
          </p>
        </div>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">
            Number of Nights
            <span className="text-muted-foreground ml-1">(1-60 nights)</span>
          </label>
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={() => onPreferredNightsChange(Math.max(1, (preferredNights || 7) - 1))}
              disabled={(preferredNights || 7) <= 1}
              className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-all ${
                (preferredNights || 7) <= 1
                  ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                  : 'border-primary bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground'
              }`}
            >
              <Icon name="Minus" size={20} />
            </button>
            
            <div className="flex-1 text-center">
              <div className="text-3xl font-bold text-foreground">{preferredNights || 7}</div>
              <div className="text-sm text-muted-foreground">Night{(preferredNights || 7) !== 1 ? 's' : ''}</div>
            </div>
            
            <button
              type="button"
              onClick={() => onPreferredNightsChange(Math.min(60, (preferredNights || 7) + 1))}
              disabled={(preferredNights || 7) >= 60}
              className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-all ${
                (preferredNights || 7) >= 60
                  ? 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                  : 'border-primary bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground'
              }`}
            >
              <Icon name="Plus" size={20} />
            </button>
          </div>
          
          {/* Helper text */}
          <div className="flex items-start space-x-2 mt-3">
            <Icon name="Info" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              How many nights would you ideally like to stay? This helps agents create accurate quotes for your accommodation needs.
            </p>
          </div>
          
          {errors?.preferredNights && (
            <div className="flex items-center space-x-2 text-error text-sm mt-2">
              <AppIcon name="AlertCircle" size={16} />
              <span>{errors?.preferredNights}</span>
            </div>
          )}
        </div>
      </div>

      {/* ✅ NEW: Customer Location Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Your Location</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Tell us where you're located so agents know which airports you can depart from
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="customerLocation" className="block text-sm font-medium text-foreground">
            City or Region <span className="text-error">*</span>
          </label>
          <input
            id="customerLocation"
            type="text"
            value={customerLocation || ''}
            onChange={(e) => onCustomerLocationChange(e?.target?.value)}
            placeholder="e.g., London, Manchester, Edinburgh"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all ${
              errors?.customerLocation 
                ? 'border-error bg-error/5' :'border-border bg-background'
            }`}
          />
          {errors?.customerLocation && (
            <div className="flex items-center space-x-2 text-error text-sm mt-2">
              <AppIcon name="AlertCircle" size={16} />
              <span>{errors?.customerLocation}</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            This helps travel agents recommend flights from your nearest airports
          </p>
        </div>
      </div>

      {/* Special Requirements Section */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Special Requirements</h3>
        <p className="text-muted-foreground mb-4">
          Let us know about any specific needs, accessibility requirements, or special occasions
        </p>
        <textarea
          value={specialRequirements}
          onChange={(e) => onSpecialRequirementsChange(e?.target?.value)}
          placeholder="e.g., Wheelchair accessibility, dietary restrictions, celebrating anniversary, traveling with elderly parents..."
          rows={4}
          className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">
            Optional - Help agents provide better recommendations
          </span>
          <span className="text-xs text-muted-foreground">
            {specialRequirements?.length}/500
          </span>
        </div>
        {errors?.specialRequirements && (
          <p className="text-error text-sm mt-2">{errors?.specialRequirements}</p>
        )}
      </div>

      {/* More Details Section */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">More Details</h3>
        <p className="text-muted-foreground mb-4">
          Share any additional information about your holiday plans
        </p>
        <textarea
          value={moreDetails || ''}
          onChange={(e) => onMoreDetailsChange(e?.target?.value)}
          placeholder="e.g., Specific hotels you're interested in, activities you want to include, travel style preferences, budget breakdown..."
          rows={5}
          maxLength={2000}
          className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">
            Optional - The more details you provide, the better we can match you with the right agent
          </span>
          <span className={`text-xs ${(moreDetails?.length || 0) > 1800 ? 'text-warning' : 'text-muted-foreground'}`}>
            {moreDetails?.length || 0}/2000
          </span>
        </div>
        {errors?.moreDetails && (
          <p className="text-error text-sm mt-2">{errors?.moreDetails}</p>
        )}
      </div>

      {/* Travel Preferences Section */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Travel Preferences</h3>
        <p className="text-muted-foreground mb-6">
          Select your interests to help agents create the perfect itinerary
        </p>
        
        <div className="space-y-6">
          {preferenceCategories?.map((category) => (
            <div key={category?.id}>
              <div className="flex items-center space-x-2 mb-3">
                <Icon name={category?.icon} size={20} className="text-primary" />
                <h4 className="font-medium text-foreground">{category?.name}</h4>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {category?.options?.map((option) => {
                  const isSelected = travelPreferences?.[category?.id]?.includes(option);
                  return (
                    <button
                      key={option}
                      onClick={() => handlePreferenceToggle(category?.id, option)}
                      className={`p-3 rounded-lg border text-sm text-left transition-all hover:scale-102 ${
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'border-primary-foreground bg-primary-foreground' 
                            : 'border-muted-foreground'
                        }`}>
                          {isSelected && (
                            <Icon name="Check" size={12} className="text-primary" />
                          )}
                        </div>
                        <span className="font-medium">{option}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preferences Summary */}
      {Object.values(travelPreferences)?.some(prefs => prefs && prefs?.length > 0) && (
        <div className="bg-primary/10 p-4 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Heart" size={20} className="text-primary" />
            <span className="font-semibold text-primary">Selected Preferences:</span>
          </div>
          <div className="space-y-1">
            {Object.entries(travelPreferences)?.map(([category, preferences]) => {
              if (!preferences || preferences?.length === 0) return null;
              const categoryName = preferenceCategories?.find(c => c?.id === category)?.name;
              return (
                <div key={category} className="text-sm">
                  <span className="font-medium text-foreground">{categoryName}: </span>
                  <span className="text-muted-foreground">{preferences?.join(', ')}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}